import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";

interface VesselEvaluationFilterSelectProps {
  label: string;
  onChange: (values: string[]) => void;
  options: string[];
  searchable?: boolean;
  selectedValues: string[];
  testId: string;
}

// Recreates the Power BI dropdown slicer's multi-select behavior without keeping duplicate local selection state.
const VesselEvaluationFilterSelect = ({
  label,
  onChange,
  options,
  searchable = false,
  selectedValues,
  testId,
}: VesselEvaluationFilterSelectProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const summary = selectedValues.length === 0
    ? "All"
    : selectedValues.length === 1
      ? selectedValues[0]
      : `${selectedValues.length} selected`;
  const visibleOptions = options.filter((option) =>
    option.toLowerCase().includes(searchQuery.trim().toLowerCase()),
  );

  const toggleOption = (option: string) => {
    const nextValues = selectedValues.includes(option)
      ? selectedValues.filter((value) => value !== option)
      : [...selectedValues, option];

    onChange(nextValues);
  };

  useEffect(() => {
    const closeWhenFocusLeaves = (event: PointerEvent) => {
      if (!detailsRef.current?.contains(event.target as Node)) {
        detailsRef.current?.removeAttribute("open");
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && detailsRef.current?.open) {
        detailsRef.current?.removeAttribute("open");
        detailsRef.current?.querySelector("summary")?.focus();
      }
    };

    document.addEventListener("pointerdown", closeWhenFocusLeaves);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeWhenFocusLeaves);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <div className="block min-w-0" data-test={testId}>
      <span className="mb-1.5 block text-xs font-semibold text-white">{label}</span>
      <details ref={detailsRef} className="group relative">
        <summary
          className="flex h-9 cursor-pointer list-none items-center justify-between border border-white/80 bg-[#07518d] px-2.5 text-xs text-white outline-none hover:bg-[#0b5e9e] focus:ring-2 focus:ring-white/70 [&::-webkit-details-marker]:hidden"
          data-test={`${testId}-summary`}
        >
          <span className="truncate">{summary}</span>
          <ChevronDownIcon className="h-4 w-4 flex-none transition-transform group-open:rotate-180" />
        </summary>
        <div className="absolute z-30 mt-1 max-h-64 w-full min-w-[180px] overflow-auto rounded border border-gray-300 bg-white p-2 text-gray-800 shadow-xl dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
          {searchable && (
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={`Search ${label}`}
              aria-label={`Search ${label}`}
              className="mb-2 h-8 w-full rounded border border-gray-300 bg-white px-2 text-xs outline-none focus:border-green focus:ring-1 focus:ring-green dark:border-gray-600 dark:bg-gray-700"
              data-test={`${testId}-search`}
            />
          )}
          <button
            type="button"
            onClick={() => onChange([])}
            className="mb-1 w-full rounded px-2 py-1.5 text-left text-xs font-semibold hover:bg-primary-10 dark:hover:bg-teal-950/50"
            data-test={`${testId}-clear`}
          >
            All
          </button>
          {visibleOptions.map((option) => (
            <label key={option} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-primary-10 dark:hover:bg-teal-950/50">
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => toggleOption(option)}
                className="h-4 w-4 rounded border-gray-400 text-green focus:ring-green"
                data-test={`${testId}-option-${option}`}
              />
              <span className="truncate" title={option}>{option}</span>
            </label>
          ))}
          {visibleOptions.length === 0 && (
            <p className="px-2 py-3 text-center text-xs text-gray-500">No matching values</p>
          )}
        </div>
      </details>
    </div>
  );
};

export default VesselEvaluationFilterSelect;
